import React from 'react';
import {
  PUBLICATION_ANALYSIS_CHART_IDS,
  PUBLICATION_ANALYSIS_SECTION_ID,
} from '../../config/publicationAnalysisChartIds';
import { useUserRole } from '../../contexts/UserRoleContext';
import { useAdminPageSettings } from '../../hooks/useAdminPageSettings';
import { CaliforniaGhgTimeSeriesStackedChart } from '../charts/CaliforniaGhgTimeSeriesStackedChart';
import { CountyInventoryVsCarbSectorCharts } from '../charts/CountyInventoryVsCarbSectorCharts';
import { CountyPerCapitaGhgMap } from '../charts/CountyPerCapitaGhgMap';
import { GhgEmissionsRankingsTable } from '../charts/GhgEmissionsRankingsTable';
import { PublicationAbatementCostScatter } from './PublicationAbatementCostScatter';
import './PublicationAnalysisPage.css';

/** Full-bleed alternating band (skipped in Download Data embed). */
function PublicationAnalysisStrip({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div className="publication-analysis-strip">
      <div className="publication-analysis-strip-inner">{children}</div>
    </div>
  );
}

export interface PublicationAnalysisPageProps {
  /** Current ClimatePlans location name */
  location: string;
  /**
   * When true, skip role gate and compact chrome so charts mount inside Download Data modal
   * (PNG registry) without duplicating access checks.
   */
  embedForDownloads?: boolean;
}

/**
 * Statewide analysis: California inventory charts, maps, abatement scatter.
 * Access: developer, super_admin, local_govt_admin, or academic (Navigation + this page).
 */
export const PublicationAnalysisPage: React.FC<PublicationAnalysisPageProps> = ({
  location,
  embedForDownloads = false,
}) => {
  const { hasAnyRole, loading } = useUserRole();
  const { isChartEnabled } = useAdminPageSettings('statewide_analysis', location || null);

  const showStacked = isChartEnabled(PUBLICATION_ANALYSIS_SECTION_ID, PUBLICATION_ANALYSIS_CHART_IDS.ghgStacked);
  const showVsCarb = isChartEnabled(PUBLICATION_ANALYSIS_SECTION_ID, PUBLICATION_ANALYSIS_CHART_IDS.vsCarb);
  const showMap = isChartEnabled(PUBLICATION_ANALYSIS_SECTION_ID, PUBLICATION_ANALYSIS_CHART_IDS.spatialMap);
  const showRankings = isChartEnabled(PUBLICATION_ANALYSIS_SECTION_ID, PUBLICATION_ANALYSIS_CHART_IDS.rankings);
  const showScatter = isChartEnabled(PUBLICATION_ANALYSIS_SECTION_ID, PUBLICATION_ANALYSIS_CHART_IDS.abatementScatter);

  const showTimeSeriesBlock = showStacked || showVsCarb;
  const showSpatialBlock = showMap || showRankings;

  if (!embedForDownloads) {
    if (loading) {
      return (
        <div className="publication-analysis-page publication-analysis-page--loading">
          <div className="loading-spinner" aria-hidden />
          <p>Checking access…</p>
        </div>
      );
    }

    if (!hasAnyRole(['developer', 'super_admin', 'local_govt_admin', 'academic'])) {
      return (
        <div className="publication-analysis-page publication-analysis-page--denied">
          <h1>Analysis</h1>
          <p className="publication-analysis-denied-text">
            This page is only available to <strong>developer</strong>, <strong>super admin</strong>,{' '}
            <strong>local government admin</strong>, or <strong>academic</strong> accounts. If you need access,
            contact your ClimatePlans administrator.
          </p>
        </div>
      );
    }
  }

  const wrap = (node: React.ReactNode) =>
    embedForDownloads ? node : <PublicationAnalysisStrip>{node}</PublicationAnalysisStrip>;

  return (
    <div
      className={`publication-analysis-page${embedForDownloads ? ' publication-analysis-page--embed' : ' publication-analysis-page--striped'}`}
    >
      {wrap(
        <header className="publication-analysis-header">
          <h1>Analysis</h1>
        </header>
      )}

      {showTimeSeriesBlock
        ? wrap(
            <section
              className="publication-analysis-section publication-analysis-section--chart"
              aria-labelledby="pub-fig-ca-ghg-ts"
            >
              <h2 id="pub-fig-ca-ghg-ts">California GHG inventory (time series)</h2>

              {showStacked ? (
                <>
                  <h3 className="publication-analysis-subh" id="pub-fig-ca-ghg-stacked">
                    Stacked by geography
                  </h3>
                  <CaliforniaGhgTimeSeriesStackedChart />
                </>
              ) : null}

              {showVsCarb ? (
                <div id="pub-fig-ca-state-sectors">
                  <CountyInventoryVsCarbSectorCharts />
                </div>
              ) : null}
            </section>
          )
        : null}

      {showSpatialBlock
        ? wrap(
            <section className="publication-analysis-section" aria-labelledby="pub-fig-spatial">
              <h2 id="pub-fig-spatial">Spatial distribution of GHG inventories</h2>
              {showMap ? <CountyPerCapitaGhgMap /> : null}
              {showRankings ? <GhgEmissionsRankingsTable location={location} /> : null}
            </section>
          )
        : null}

      {showScatter ? wrap(<PublicationAbatementCostScatter />) : null}
    </div>
  );
};

export default PublicationAnalysisPage;
